import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Locale } from '@monrdv/shared';
import { ApiError, request } from '../lib/api.js';
import { useCan } from '../lib/session.js';

/**
 * Booking and moving visits on a job.
 *
 * The job page has always listed its visits and offered nothing to do about them, so a
 * coordinator could see that a job needed a technician and had no way to send one. The
 * endpoints existed the whole time.
 *
 * **A booking has a precision, and that is the whole shape of this form.** 539 of the imported
 * bookings are a day plus a period — the customer was told "mercredi AM" — and 1 726 are an
 * exact slot. The API validates the two together and refuses a period on an exact appointment,
 * so the control offers the promise first and the time second, rather than collecting a
 * timestamp and inventing a precision the customer was never given.
 *
 * **Assignees come from `/jobs/filter-options`, not `/team`.** `/team` requires `user.manage`,
 * which a coordinator who books visits all day has no reason to hold; the filter endpoint
 * already lists active members behind plain authentication, and narrows to just themselves for
 * a technician — which is the correct answer for a technician anyway.
 *
 * **Cancelling asks why.** A visit that was never done is a fact somebody will ask about weeks
 * later, and `cancelledReason` is already shown on the job. An empty cancellation is a hole in
 * the record that nobody can fill in afterwards.
 */

type Visit = {
  id: string;
  startsAt: string;
  endsAt: string | null;
  status: string | null;
  assignee: { id: string; name: string; color?: string | null } | null;
  crew: Array<{ id: string; name: string }>;
  note: string | null;
  completedAt: string | null;
  cancelledReason: string | null;
};

type Assignees = { assignees: Array<{ id: string; name: string; initials: string }> };

type Precision = 'exact' | 'period' | 'day';
type Period = 'am' | 'pm' | 'day' | 'evening' | 'week';

const PERIODS: Period[] = ['am', 'pm', 'day', 'evening', 'week'];

/** The statuses a coordinator sets from here. The rest are the field app's to record. */
const OPEN_STATUSES = new Set(['scheduled', 'confirmed', null]);

/**
 * A local date and time as an offset-bearing instant.
 *
 * `datetime-local` yields `2026-08-27T09:00` with no zone, and the API requires an offset. The
 * browser's own zone is the right one: the person booking is in the same place as the work.
 */
function toInstant(day: string, time: string): string | null {
  if (!day) return null;
  const local = new Date(`${day}T${time || '00:00'}`);
  if (Number.isNaN(local.getTime())) return null;
  return local.toISOString();
}

/** Splits an instant back into the two controls, in local time. */
function fromInstant(iso: string): { day: string; time: string } {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    day: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

export function VisitScheduler({
  jobId,
  visits,
  locale,
  formatWindow,
}: {
  jobId: string;
  visits: Visit[];
  locale: Locale;
  /** Reused from the job screen so a booked visit reads exactly like the ones beside it. */
  formatWindow: (startsAt: string | null, endsAt: string | null, locale: Locale) => string | null;
}) {
  const { t: translate } = useTranslation();
  const t = translate as (key: string, vars?: Record<string, string | number>) => string;
  const client = useQueryClient();
  const can = useCan();
  /*
   * Booking is a coordinator's act. A technician holds `job.complete` and reads this panel to
   * see their own day — showing them controls the API would refuse is worse than not showing
   * them at all.
   */
  const maySchedule = can('job.assign') || can('job.write');
  const [booking, setBooking] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const options = useQuery({
    queryKey: ['jobFilterOptions'],
    queryFn: () => request<Assignees>('/jobs/filter-options'),
    staleTime: 10 * 60_000,
    enabled: maySchedule,
  });

  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['job', jobId] });
    void client.invalidateQueries({ queryKey: ['board'] });
    void client.invalidateQueries({ queryKey: ['jobs'] });
    void client.invalidateQueries({ queryKey: ['appointments'] });
    void client.invalidateQueries({ queryKey: ['today'] });
  };

  const fail = (err: unknown) =>
    setError(err instanceof ApiError && err.message ? err.message : t('visit.failed'));

  return (
    <div className="visits">
      {error && (
        <p className="recform__error" role="alert">
          {error}
        </p>
      )}

      <ul className="visits__list">
        {visits.map((visit) => (
          <li key={visit.id} className="visits__item">
            <div className="visits__row">
              <span className="dimension tnum">{formatWindow(visit.startsAt, visit.endsAt, locale) ?? ''}</span>
              <span className="visits__who">
                {visit.assignee?.name ?? t('job.unassigned')}
                {visit.crew
                  .filter((m) => m.id !== visit.assignee?.id)
                  .map((m) => `, ${m.name}`)
                  .join('')}
                {visit.status ? ` · ${t(`visit.status.${visit.status}`, { defaultValue: visit.status })}` : ''}
                {visit.cancelledReason ? ` · ${visit.cancelledReason}` : ''}
                {visit.note ? ` · ${visit.note}` : ''}
              </span>
              {/*
                Only an open visit can be moved. A completed one is a record of what happened,
                and rescheduling the past is how a history stops being trustworthy.
              */}
              {maySchedule && OPEN_STATUSES.has(visit.status) && (
                <button
                  type="button"
                  className="recform__add"
                  onClick={() => {
                    setError(null);
                    setEditing(editing === visit.id ? null : visit.id);
                  }}
                >
                  {editing === visit.id ? t('common.close') : t('visit.move')}
                </button>
              )}
            </div>

            {editing === visit.id && (
              <VisitForm
                t={t}
                mode="patch"
                visit={visit}
                assignees={options.data?.assignees ?? []}
                onDone={() => {
                  setEditing(null);
                  refresh();
                }}
                onError={fail}
                url={`/appointments/${visit.id}`}
              />
            )}
          </li>
        ))}
        {!visits.length && <li className="visits__none">{t('visit.none')}</li>}
      </ul>

      {!maySchedule ? null : booking ? (
        <VisitForm
          t={t}
          mode="post"
          jobId={jobId}
          assignees={options.data?.assignees ?? []}
          onDone={() => {
            setBooking(false);
            refresh();
          }}
          onError={fail}
          url="/appointments"
          onCancel={() => setBooking(false)}
        />
      ) : (
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => {
            setError(null);
            setBooking(true);
          }}
        >
          {t('visit.book')}
        </button>
      )}
    </div>
  );
}

/**
 * One form for booking and for moving.
 *
 * The same fields mean the same thing in both cases, and the only real difference is which
 * verb it sends — so it is one component rather than two that drift. A patch sends every field
 * it shows: the endpoint is `.partial()`, but a reschedule that moved the day and silently kept
 * a stale period would promise the customer something nobody said.
 */
function VisitForm({
  t,
  mode,
  url,
  jobId,
  visit,
  assignees,
  onDone,
  onError,
  onCancel,
}: {
  t: (key: string, vars?: Record<string, string | number>) => string;
  mode: 'post' | 'patch';
  url: string;
  jobId?: string;
  visit?: Visit;
  assignees: Array<{ id: string; name: string }>;
  onDone: () => void;
  onError: (err: unknown) => void;
  onCancel?: () => void;
}) {
  const existing = visit ? fromInstant(visit.startsAt) : null;
  const [precision, setPrecision] = useState<Precision>('exact');
  const [period, setPeriod] = useState<Period>('am');
  const [day, setDay] = useState(existing?.day ?? '');
  const [time, setTime] = useState(existing?.time ?? '09:00');
  const [endTime, setEndTime] = useState(visit?.endsAt ? fromInstant(visit.endsAt).time : '');
  const [assigneeId, setAssigneeId] = useState(visit?.assignee?.id ?? '');
  const [note, setNote] = useState(visit?.note ?? '');
  const [cancelReason, setCancelReason] = useState('');

  const startsAt = toInstant(day, precision === 'exact' ? time : '08:00');
  const endsAt = precision === 'exact' && endTime ? toInstant(day, endTime) : null;

  const body = () => ({
    ...(mode === 'post' ? { jobId } : {}),
    startsAt,
    endsAt,
    precision,
    // The API refuses a period on an exact appointment and requires one on a period
    // appointment, so this is null exactly when the promise had no range.
    period: precision === 'exact' ? null : period,
    assigneeId: assigneeId || null,
    note: note.trim() || null,
  });

  const save = useMutation({
    mutationFn: () => request(url, { method: mode === 'post' ? 'POST' : 'PATCH', body: JSON.stringify(body()) }),
    onSuccess: onDone,
    onError,
  });

  const cancel = useMutation({
    mutationFn: () =>
      request(url, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'cancelled', cancelledReason: cancelReason.trim() }),
      }),
    onSuccess: onDone,
    onError,
  });

  const busy = save.isPending || cancel.isPending;
  const ready = !!startsAt && (precision !== 'exact' || !!time);

  return (
    <div className="visits__form">
      <div className="recform__pair">
        <div className="recform__field">
          <label className="recform__label">{t('visit.precision')}</label>
          <select
            className="recform__select"
            value={precision}
            disabled={busy}
            onChange={(e) => setPrecision(e.target.value as Precision)}
          >
            <option value="exact">{t('visit.precisions.exact')}</option>
            <option value="period">{t('visit.precisions.period')}</option>
            <option value="day">{t('visit.precisions.day')}</option>
          </select>
          {/* Says what the customer was told, which is the thing that must not be invented. */}
          <span className="recform__hint">{t('visit.precisionHint')}</span>
        </div>

        <div className="recform__field">
          <label className="recform__label">{t('visit.day')}</label>
          <input
            type="date"
            className="recform__input"
            value={day}
            disabled={busy}
            onChange={(e) => setDay(e.target.value)}
          />
        </div>
      </div>

      <div className="recform__pair">
        {precision === 'exact' ? (
          <>
            <div className="recform__field">
              <label className="recform__label">{t('visit.startTime')}</label>
              <input
                type="time"
                className="recform__input"
                value={time}
                disabled={busy}
                onChange={(e) => setTime(e.target.value)}
              />
            </div>
            <div className="recform__field">
              <label className="recform__label">
                {t('visit.endTime')} <span className="recform__optional">{t('common.optional')}</span>
              </label>
              <input
                type="time"
                className="recform__input"
                value={endTime}
                disabled={busy}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </>
        ) : (
          <div className="recform__field">
            <label className="recform__label">{t('visit.period')}</label>
            <select
              className="recform__select"
              value={period}
              disabled={busy}
              onChange={(e) => setPeriod(e.target.value as Period)}
            >
              {PERIODS.map((value) => (
                <option key={value} value={value}>
                  {t(`visit.periods.${value}`)}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="recform__field">
          <label className="recform__label">{t('visit.assignee')}</label>
          <select
            className="recform__select"
            value={assigneeId}
            disabled={busy}
            onChange={(e) => setAssigneeId(e.target.value)}
          >
            <option value="">{t('job.unassigned')}</option>
            {assignees.map((person) => (
              <option key={person.id} value={person.id}>
                {person.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="recform__field">
        <label className="recform__label">
          {t('visit.note')} <span className="recform__optional">{t('common.optional')}</span>
        </label>
        <input
          className="recform__input"
          value={note}
          disabled={busy}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t('visit.notePlaceholder')}
        />
      </div>

      <div className="recform__row">
        <button type="button" className="btn btn--primary" disabled={busy || !ready} onClick={() => save.mutate()}>
          {mode === 'post' ? t('visit.book') : t('common.save')}
        </button>
        {onCancel && (
          <button type="button" className="btn btn--ghost" disabled={busy} onClick={onCancel}>
            {t('common.cancel')}
          </button>
        )}
        <span className="recform__spacer" />
      </div>

      {/*
        Cancelling a booked visit, with its reason. Kept in the same panel rather than behind a
        separate control: somebody who opened this to move a visit is often the same person who
        has just learned it is not happening at all.
      */}
      {mode === 'patch' && (
        <div className="visits__cancel">
          <div className="recform__field">
            <label className="recform__label">{t('visit.cancelReason')}</label>
            <input
              className="recform__input"
              value={cancelReason}
              disabled={busy}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder={t('visit.cancelPlaceholder')}
            />
          </div>
          <button
            type="button"
            className="btn btn--danger"
            disabled={busy || !cancelReason.trim()}
            onClick={() => cancel.mutate()}
          >
            {t('visit.cancel')}
          </button>
        </div>
      )}
    </div>
  );
}
